// src/ConsentGate.jsx
// Render once in App.jsx, right after <UsernameGate />.
//  - Everyone confirms their date of birth once (after choosing a username).
//  - 13 to 17: must name a parent; blocked until the parent approves.
//  - Under 13: blocked.
// Reads: 0 once the localStorage flag is set (adults); otherwise one live
// listener on users/{uid} so approvals and revokes apply instantly.
import React, { useEffect, useState } from "react";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import { getFirestore, doc, onSnapshot } from "firebase/firestore";
import {
  ConsentError,
  MIN_AGE,
  ADULT_AGE,
  RETRY_AFTER_MS,
  calcAge,
  saveAge,
  requestParentConsent,
  isConsentDone,
  setConsentDone,
  isUnder13Blocked,
  setUnder13Blocked,
} from "./utils/consentService";
import "./ConsentGate.css";

const todayStr = () => new Date().toISOString().slice(0, 10);

const Shell = ({ title, children }) => {
  // Lock page scroll while the screen is up.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  return (
    <div className="cg-overlay" role="dialog" aria-modal="true" aria-labelledby="cg-title">
      <div className="cg-card">
        <h2 id="cg-title" className="cg-title">{title}</h2>
        {children}
      </div>
    </div>
  );
};

const SignOutButton = () => (
  <button type="button" className="cg-link" onClick={() => signOut(getAuth())}>
    Sign out
  </button>
);

/* ---------- Step 1: date of birth (+ parent for 13 to 17) ---------- */
const AgeStep = ({ uid, onBlocked }) => {
  const [dob, setDob] = useState("");
  const [parent, setParent] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const age = calcAge(dob);
  const needsParent = age !== null && age >= MIN_AGE && age < ADULT_AGE;

  const submit = async () => {
    if (saving) return;
    setError("");
    if (age === null) {
      setError("Enter a valid date of birth.");
      return;
    }
    if (age < MIN_AGE) {
      setUnder13Blocked(uid);
      onBlocked();
      return;
    }
    setSaving(true);
    try {
      await saveAge({ uid, dob, parentUsername: parent });
      // The live listener in ConsentGate moves to the next screen.
    } catch (e) {
      if (e instanceof ConsentError) {
        setError(e.message);
      } else {
        console.error("Age save failed", e);
        setError("Couldn’t save. Check your connection and try again.");
      }
      setSaving(false);
    }
  };

  return (
    <Shell title="Confirm your age">
      <p className="cg-sub">
        Enter your date of birth. It stays private, and you can’t change it later.
      </p>

      <label className="cg-label" htmlFor="cg-dob">Date of birth</label>
      <input
        id="cg-dob"
        className="cg-input"
        type="date"
        value={dob}
        min="1900-01-01"
        max={todayStr()}
        onChange={(e) => setDob(e.target.value)}
        disabled={saving}
      />

      {needsParent && (
        <>
          <p className="cg-sub">
            Because you’re under 18, a parent or guardian needs to approve your
            account. They need a Trescend account and must be 18 or older.
          </p>
          <label className="cg-label" htmlFor="cg-parent">
            Parent or guardian’s username
          </label>
          <div className="cg-input-wrap">
            <span className="cg-at">@</span>
            <input
              id="cg-parent"
              className="cg-input cg-input-bare"
              type="text"
              value={parent}
              onChange={(e) =>
                setParent(e.target.value.toLowerCase().replace(/\s/g, ""))
              }
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              autoComplete="off"
              disabled={saving}
            />
          </div>
        </>
      )}

      {error && <p className="cg-error">{error}</p>}

      <button type="button" className="cg-btn" onClick={submit} disabled={saving || !dob}>
        {saving ? "Saving…" : "Continue"}
      </button>
      <SignOutButton />
    </Shell>
  );
};

/* ---------- Step 2: waiting / declined / revoked / change parent ---------- */
const ParentStep = ({ uid, data }) => {
  const status = data.consentStatus || "none";
  const [editing, setEditing] = useState(status === "none");
  const [parent, setParent] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const who = data.parentUsername ? `@${data.parentUsername}` : "your parent";
  const decidedAtMs = data.consentDecidedAt?.toMillis?.() ?? null;
  const waitMs =
    (status === "denied" || status === "revoked") && decidedAtMs
      ? decidedAtMs + RETRY_AFTER_MS - Date.now()
      : 0;
  const waitHours = waitMs > 0 ? Math.ceil(waitMs / 3600000) : 0;

  const send = async () => {
    if (saving) return;
    setError("");
    setSaving(true);
    try {
      await requestParentConsent({
        uid,
        parentUsername: parent,
        status,
        decidedAtMs,
      });
      setParent("");
      setEditing(false);
    } catch (e) {
      if (e instanceof ConsentError) {
        setError(e.message);
      } else {
        console.error("Consent request failed", e);
        setError("Couldn’t send the request. Check your connection and try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <Shell title="Choose a parent or guardian">
        <p className="cg-sub">
          They’ll get your request in their Trescend inbox. They need an account
          and must be 18 or older.
        </p>
        <label className="cg-label" htmlFor="cg-parent2">
          Parent or guardian’s username
        </label>
        <div className="cg-input-wrap">
          <span className="cg-at">@</span>
          <input
            id="cg-parent2"
            className="cg-input cg-input-bare"
            type="text"
            value={parent}
            onChange={(e) =>
              setParent(e.target.value.toLowerCase().replace(/\s/g, ""))
            }
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            autoComplete="off"
            disabled={saving}
            autoFocus
          />
        </div>
        {error && <p className="cg-error">{error}</p>}
        <button type="button" className="cg-btn" onClick={send} disabled={saving || !parent}>
          {saving ? "Sending…" : "Send request"}
        </button>
        {status !== "none" && (
          <button
            type="button"
            className="cg-link"
            onClick={() => {
              setEditing(false);
              setError("");
            }}
            disabled={saving}
          >
            Cancel
          </button>
        )}
        <SignOutButton />
      </Shell>
    );
  }

  const copy = {
    pending: {
      title: "Waiting for your parent",
      text: `Your request is with ${who}. They can approve it from their Trescend inbox, and you’ll get in as soon as they do.`,
      action: "Change parent",
    },
    denied: {
      title: "Request declined",
      text: `${who} declined your request. You can ask again or choose someone else.`,
      action: "Ask again",
    },
    revoked: {
      title: "Access turned off",
      text: `${who} turned off your access. You can ask again or choose someone else.`,
      action: "Ask again",
    },
  }[status] || {
    title: "Choose a parent or guardian",
    text: "A parent or guardian needs to approve your account.",
    action: "Choose parent",
  };

  return (
    <Shell title={copy.title}>
      <p className="cg-sub">{copy.text}</p>
      {waitHours > 0 && (
        <p className="cg-hint">
          You can ask again in {waitHours} hour{waitHours === 1 ? "" : "s"}.
        </p>
      )}
      <button
        type="button"
        className="cg-btn"
        onClick={() => setEditing(true)}
        disabled={waitHours > 0}
      >
        {copy.action}
      </button>
      <SignOutButton />
    </Shell>
  );
};

/* ---------- Gate ---------- */
const ConsentGate = () => {
  const [uid, setUid] = useState(null);
  const [data, setData] = useState(null);
  const [blocked, setBlocked] = useState(false);

  useEffect(() => {
    let unsubDoc = null;

    const unsubAuth = onAuthStateChanged(getAuth(), (user) => {
      if (unsubDoc) {
        unsubDoc();
        unsubDoc = null;
      }
      setUid(null);
      setData(null);
      setBlocked(false);
      if (!user) return;
      if (isConsentDone(user.uid)) return; // adult, already confirmed: 0 reads

      setUid(user.uid);
      unsubDoc = onSnapshot(
        doc(getFirestore(), "users", user.uid),
        (snap) => {
          const d = snap.exists() ? snap.data() : {};
          if (d.isMinor === false) setConsentDone(user.uid);
          setData(d);
        },
        (e) => console.error("Consent check failed", e)
      );
    });

    return () => {
      unsubAuth();
      if (unsubDoc) unsubDoc();
    };
  }, []);

  if (!uid || !data) return null;
  // The username popup comes first; this appears right after it is saved.
  if (!data.username) return null;

  if (blocked || isUnder13Blocked(uid)) {
    return (
      <Shell title="Trescend is for ages 13 and up">
        <p className="cg-sub">
          We can’t create an account for anyone under 13.
        </p>
        <SignOutButton />
      </Shell>
    );
  }

  if (data.isMinor === false) return null;

  if (data.isMinor !== true) {
    return <AgeStep uid={uid} onBlocked={() => setBlocked(true)} />;
  }

  if (data.consentStatus === "approved") return null;

  return <ParentStep uid={uid} data={data} />;
};

export default ConsentGate;